import * as Sentry from '@sentry/nextjs';

import {
  CreateNodesBatchModel,
  CreateOrUpdateStrategyNodeModel,
  CreateOrUpdateStrategyPathModel,
  NodeModel,
  StrategyModel,
  VersionedEntityType
} from '@models';
import { APIRequestLabel } from '../consts';
import { callHttpDelete, callHttpGet, callHttpPatch, callHttpPost } from '../http';

const getStrategy = (get, set) => {
  return async (
    strategyEntityId: string,
    params: { maxVersionType?: VersionedEntityType }
  ): Promise<StrategyModel> => {
    params = params || {};

    let qParams = {};
    if (params.maxVersionType) {
      qParams['maxVersionType'] = params.maxVersionType;
    }

    let q = new URLSearchParams(qParams).toString();

    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_STRATEGY,
      `/strategies/${strategyEntityId}?${q}`
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_STRATEGY];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const publishStrategy = (get, set) => {
  return async (strategyEntityId: string): Promise<boolean> => {
    await callHttpPost(
      get,
      set,
      APIRequestLabel.PUBLISH_STRATEGY,
      `/strategies/${strategyEntityId}/publish`,
      {}
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.PUBLISH_STRATEGY];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      throw new Error('Failed to publish the strategy');
    }

    return apiCallStatus.response?.success === true;
  };
};

const createStrategyPath = (get, set) => {
  return async (
    strategyEntityId: string,
    path: CreateOrUpdateStrategyPathModel
  ): Promise<{ entityId: string }> => {
    await callHttpPost(
      get,
      set,
      APIRequestLabel.CREATE_STRATEGY_PATH,
      `/strategies/${strategyEntityId}/paths`,
      path
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.CREATE_STRATEGY_PATH];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const updateStrategyPath = (get, set) => {
  return async (
    strategyEntityId: string,
    pathEntityId: string,
    path: CreateOrUpdateStrategyPathModel
  ): Promise<void> => {
    await callHttpPatch(
      get,
      set,
      APIRequestLabel.UPDATE_STRATEGY_PATH,
      `/strategies/${strategyEntityId}/paths/${pathEntityId}`,
      path
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.UPDATE_STRATEGY_PATH];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const deleteStrategyPath = (get, set) => {
  return async (strategyEntityId: string, pathEntityId: string): Promise<void> => {
    await callHttpDelete(
      get,
      set,
      APIRequestLabel.DELETE_STRATEGY_PATH,
      `/strategies/${strategyEntityId}/paths/${pathEntityId}`
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.DELETE_STRATEGY_PATH];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const cleanupNode = (
  node: NodeModel | CreateOrUpdateStrategyNodeModel
): CreateOrUpdateStrategyNodeModel => {
  let allowedFields = [
    'type',
    'pathEntityId',
    'activationRuleEntityId',
    'messageTemplate',
    'actionMoveToPathEntityId',
    'isRequired',
    'taskEntityId',
    'groupEntityId',
    'nextEntityId'
  ];

  let cleanedNode = {};
  for (let field of allowedFields) {
    if (node[field] !== undefined) {
      cleanedNode[field] = node[field];
    }
  }

  return cleanedNode;
};

const createStrategyNodesBatch = (get, set) => {
  return async (
    strategyEntityId: string,
    pathEntityId: string,
    nodesBatch: CreateNodesBatchModel
  ): Promise<{ entityId: string }> => {
    await callHttpPost(
      get,
      set,
      APIRequestLabel.CREATE_STRATEGY_NODES_BATCH,
      `/strategies/${strategyEntityId}/paths/${pathEntityId}/nodes/batch`,
      nodesBatch
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.CREATE_STRATEGY_NODES_BATCH];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const createStrategyNode = (get, set) => {
  return async (
    strategyEntityId: string,
    pathEntityId: string,
    node: CreateOrUpdateStrategyNodeModel
  ): Promise<{ entityId: string }> => {
    await callHttpPost(
      get,
      set,
      APIRequestLabel.CREATE_STRATEGY_NODE,
      `/strategies/${strategyEntityId}/paths/${pathEntityId}/nodes`,
      cleanupNode(node)
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.CREATE_STRATEGY_NODE];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const updateStrategyNode = (get, set) => {
  return async (
    strategyEntityId: string,
    pathEntityId: string,
    nodeEntityId: string,
    node: CreateOrUpdateStrategyNodeModel
  ): Promise<void> => {
    await callHttpPatch(
      get,
      set,
      APIRequestLabel.UPDATE_STRATEGY_NODE,
      `/strategies/${strategyEntityId}/paths/${pathEntityId}/nodes/${nodeEntityId}`,
      cleanupNode(node)
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.UPDATE_STRATEGY_NODE];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const moveStrategyNode = (get, set) => {
  return async (
    strategyEntityId: string,
    pathEntityId: string,
    nodeEntityId: string,
    node: CreateOrUpdateStrategyNodeModel
  ): Promise<void> => {
    await callHttpPatch(
      get,
      set,
      APIRequestLabel.UPDATE_STRATEGY_NODE,
      `/strategies/${strategyEntityId}/paths/${pathEntityId}/nodes/${nodeEntityId}`,
      cleanupNode(node)
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.UPDATE_STRATEGY_NODE];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const deleteStrategyNode = (get, set) => {
  return async (
    strategyEntityId: string,
    pathEntityId: string,
    nodeEntityId: string
  ): Promise<void> => {
    await callHttpDelete(
      get,
      set,
      APIRequestLabel.DELETE_STRATEGY_NODE,
      `/strategies/${strategyEntityId}/paths/${pathEntityId}/nodes/${nodeEntityId}`
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.DELETE_STRATEGY_NODE];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const publishTask = (get, set) => {
  return async (taskEntityId: string): Promise<boolean> => {
    try {
      await get().imprvApi.publishTask(taskEntityId);
      return true;
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  };
};

export {
  createStrategyNode,
  createStrategyNodesBatch,
  createStrategyPath,
  deleteStrategyNode,
  deleteStrategyPath,
  getStrategy,
  publishStrategy,
  publishTask,
  updateStrategyNode,
  updateStrategyPath
};
